import { View } from 'web-mojo';

/**
 * SplitRouteModal — move a selection of pending stops to a second operator.
 * Emits 'split' on success.
 */
export default class SplitRouteModal extends View {
    constructor({ route }) {
        super();
        this.route = route;
        this.operators = [];
    }

    async onInit() {
        const [opResponse, stopsResponse] = await Promise.all([
            app.api.get('/api/routes/operator', { group: app.groupId(), status: 'active', size: 200 }),
            app.api.get('/api/routes/stop', { route: this.route.id, sort: 'sequence_position', size: 200 }),
        ]);
        if (opResponse.success && opResponse.data.status) {
            const currentOpId = this.route.operator?.id;
            this.operators = (opResponse.data.data || []).filter(op => op.id !== currentOpId);
        }
        if (stopsResponse.success && stopsResponse.data.status) {
            this.route.stops_list = stopsResponse.data.data || [];
        }
    }

    async onActionConfirmSplit() {
        const operatorId = this.element.querySelector('[name=target_operator]')?.value;
        if (!operatorId) {
            app.toast.warning('Select an operator to receive the stops');
            return;
        }

        const checkboxes = this.element.querySelectorAll('[name=stop_ids]:checked');
        const stopIds = Array.from(checkboxes).map(cb => parseInt(cb.value));
        if (stopIds.length === 0) {
            app.toast.warning('Select at least one stop to transfer');
            return;
        }

        const response = await app.api.patch(`/api/routes/route/${this.route.id}`, {
            action: 'split',
            stop_ids: stopIds,
            target_operator_id: parseInt(operatorId),
        });

        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to split route');
            return;
        }

        this.emit('split', response.data.data);
    }

    set template(v) { this._t = v; }

    get template() {
        const stops = this.route.stops_list || [];
        const pendingStops = stops.filter(s => s.status === 'pending');
        const lockedStops = stops.filter(s => s.status !== 'pending');

        const currentOperator = this.route.operator?.display_name
            || this.route.operator?.user?.username
            || 'Current Operator';

        const pendingRows = pendingStops.map((stop, i) => {
            const label = stop.label || stop.location?.name || `Stop ${stops.indexOf(stop) + 1}`;
            return `
                <div class="form-check py-1 border-bottom">
                    <input class="form-check-input" type="checkbox" name="stop_ids"
                        value="${stop.id}" id="stop_${stop.id}">
                    <label class="form-check-label small" for="stop_${stop.id}">
                        <span class="fw-semibold">${label}</span>
                        <span class="text-muted ms-1">${stop.activity_type || ''}</span>
                    </label>
                </div>
            `;
        }).join('');

        const lockedRows = lockedStops.map(stop => {
            const label = stop.label || stop.location?.name || 'Stop';
            const statusLabel = { completed: 'Completed', in_progress: 'In Progress', arrived: 'Arrived' }[stop.status] || stop.status;
            return `
                <div class="py-1 border-bottom d-flex align-items-center gap-2 opacity-50">
                    <input class="form-check-input" type="checkbox" disabled>
                    <span class="small">${label}</span>
                    <span class="badge bg-light text-dark border ms-auto small">${statusLabel}</span>
                </div>
            `;
        }).join('');

        const operatorOptions = this.operators.map(op =>
            `<option value="${op.id}">${op.display_name || op.id}</option>`
        ).join('');

        const pendingCount = pendingStops.length;

        return `
            <div class="p-3">
                ${pendingCount === 0 ? `
                    <div class="alert alert-warning small">
                        No pending stops available to transfer. Stops that are completed or in progress cannot be moved.
                    </div>
                ` : `
                    <p class="small text-muted mb-3">
                        Select stops to reassign to a second operator.
                        <strong>${currentOperator}</strong> will retain any unselected stops.
                    </p>
                `}

                <div class="mb-3 border rounded px-3 py-2">
                    ${lockedRows}
                    ${pendingRows}
                    ${pendingCount === 0 ? '<div class="text-muted small py-2">No pending stops.</div>' : ''}
                </div>

                <div class="mb-4">
                    <label class="form-label">Assign selected stops to</label>
                    <select class="form-select" name="target_operator">
                        <option value="">Select operator…</option>
                        ${operatorOptions}
                    </select>
                </div>

                <div class="alert alert-info small py-2 mb-3">
                    <i class="bi bi-bell-fill me-1"></i>
                    Both operators will receive push notifications.
                </div>

                <div class="d-flex justify-content-end gap-2">
                    <button type="button" class="btn btn-primary" data-action="confirm-split"
                        ${pendingCount === 0 ? 'disabled' : ''}>
                        <i class="bi bi-scissors me-1"></i>Confirm Split
                    </button>
                </div>
            </div>
        `;
    }
}
